import DOMPurify from 'dompurify';

/**
 * Sanitization utility for user-influenced content
 * Prevents XSS attacks from AI responses, user input, or external data
 */

// Configure DOMPurify to only allow safe tags
const ALLOWED_TAGS = ['b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'ol', 'li', 'span'];
const ALLOWED_ATTR = ['class'];

/**
 * Sanitize HTML content - removes all potentially dangerous elements
 * Use for AI responses, user-generated content, or any external data
 */
export const sanitizeHtml = (dirty: string): string => {
  if (typeof dirty !== 'string') return '';
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
  });
};

/**
 * Sanitize text content - strips all HTML and returns plain text
 * Use for display in text elements, titles, descriptions
 */
export const sanitizeText = (dirty: string): string => {
  if (typeof dirty !== 'string') return '';
  // First sanitize to remove dangerous content, then strip all tags
  return DOMPurify.sanitize(dirty, { ALLOWED_TAGS: [] });
};

/**
 * Sanitize and truncate text for safe display
 * Useful for alert messages, notification content, etc.
 */
export const sanitizeAndTruncate = (dirty: string, maxLength: number = 500): string => {
  const clean = sanitizeText(dirty);
  if (clean.length <= maxLength) return clean;
  return clean.substring(0, maxLength) + '...';
};

/**
 * Sanitize transaction narration - removes HTML-like characters
 * and limits length for database safety
 */
export const sanitizeNarration = (text: string): string => {
  if (typeof text !== 'string') return '';
  return text
    .replace(/[<>]/g, '') // Remove HTML-like chars
    .replace(/['"]/g, "'") // Normalize quotes
    .substring(0, 500); // Limit length
};

/**
 * Sanitize for use in URL parameters
 */
export const sanitizeUrlParam = (param: string): string => {
  if (typeof param !== 'string') return '';
  return encodeURIComponent(param.substring(0, 200));
};

export default {
  sanitizeHtml,
  sanitizeText,
  sanitizeAndTruncate,
  sanitizeNarration,
  sanitizeUrlParam,
};
